/* global debounce */
if (!customElements.get('company-billing-info')) {
  class CompanyBillingInfo extends HTMLElement {
    constructor() {
      super();
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };
      this.init();
    }

    init() {
      const inputs = this.querySelectorAll('.company-billing-info__input');
      if (inputs.length === 0) return;

      this.debouncedHandleInputChange = debounce(this.handleInputChange.bind(this), 500);
      
      inputs.forEach((input) => {
        if (input.id === 'dic') {
          input.addEventListener('input', (evt) => {
            this.filterDicInput(evt.target);
            this.validateAllInputs();
            this.debouncedHandleInputChange(evt);
          });
        } else if (input.id === 'ico') {
          input.addEventListener('input', (evt) => {
            this.filterIcoInput(evt.target);
            this.validateAllInputs();
            this.debouncedHandleInputChange(evt);
          });
        } else {
          input.addEventListener('input', (evt) => {
            this.validateAllInputs();
            this.debouncedHandleInputChange(evt);
          });
        }

        input.addEventListener('blur', (evt) => {
          this.validateAllInputs();
        });
        input.addEventListener('invalid', (evt) => {
          evt.preventDefault();
          const value = evt.target.value.trim();
          if (value !== '') {
            this.showValidationError(evt.target);
          }
        });
      });

      const formId = inputs[0].getAttribute('form');
      if (formId) {
        const form = document.getElementById(formId);
        if (form) {
          form.addEventListener('submit', (evt) => {
            this.validateAllInputs();
            let hasErrors = false;

            inputs.forEach((input) => {
              const value = input.value.trim();
              
              // DIČ is optional - only validate format when value exists
              if (input.id === 'dic') {
                if (value === '') {
                  input.setCustomValidity('');
                } else if (!this.isInputValid(input, value)) {
                  input.setCustomValidity(input.dataset.errorMessage || input.title || '');
                  hasErrors = true;
                } else {
                  input.setCustomValidity('');
                }
                return;
              }
              
              const hasOtherValue = this.hasOtherFieldValue(input);

              // Require IČO and company name when other fields are filled, but DIČ is optional
              if (hasOtherValue && value === '') {
                input.setCustomValidity(input.dataset.requiredMessage || 'This field is required.');
                hasErrors = true;
              } else if (value !== '' && !this.isInputValid(input, value)) {
                // Validate format when field has a value
                input.setCustomValidity(input.dataset.errorMessage || input.title || '');
                hasErrors = true;
              } else {
                input.setCustomValidity('');
              }
            });

            if (hasErrors) {
              evt.preventDefault();
            }
          });
        }
      }
    }

    validateInput(input) {
      const value = input.value.trim();
      
      // DIČ is optional - only validate format when value exists
      if (input.id === 'dic') {
        if (value === '') {
          this.hideError(input);
          return;
        }
        // Validate format only when field has a value
        const isValid = this.isInputValid(input, value);
        if (isValid) {
          this.hideError(input);
        } else {
          this.showValidationError(input);
        }
        return;
      }
      
      const hasOtherFieldValue = this.hasOtherFieldValue(input);
      
      if (value === '') {
        if (hasOtherFieldValue) {
          this.showRequiredError(input);
        } else {
          this.hideError(input);
        }
        return;
      }

      const isValid = this.isInputValid(input, value);
      if (isValid) {
        this.hideError(input);
      } else {
        this.showValidationError(input);
      }
    }

    isInputValid(input, value) {
      const inputId = input.id;
      
      if (inputId === 'ico') {
        return /^[0-9]{8}$/.test(value);
      } else if (inputId === 'dic') {
        // DIČ format: CZ (8-10 digits) or SK (10 digits)
        return /^(CZ[0-9]{8,10}|SK[0-9]{10})$/.test(value);
      } else if (inputId === 'firma') {
        return value.length >= 2 && value.length <= 100;
      }
      
      return true;
    }

    hasOtherFieldValue(currentInput) {
      const inputs = this.querySelectorAll('.company-billing-info__input');
      return Array.from(inputs).some((inp) => inp !== currentInput && inp.value.trim() !== '');
    }

    hasAnyFieldValue() {
      const inputs = this.querySelectorAll('.company-billing-info__input');
      return Array.from(inputs).some((inp) => inp.value.trim() !== '');
    }

    showError(input, message) {
      const errorElement = input.parentElement.querySelector('[data-error-element]');
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.classList.add('is-visible');
        input.setAttribute('aria-invalid', 'true');
      }
    }

    showRequiredError(input) {
      const requiredMessage = input.dataset.requiredMessage || 'This field is required when other fields are filled.';
      this.showError(input, requiredMessage);
    }

    showValidationError(input) {
      const errorMessage = input.dataset.errorMessage || input.title || 'Please match the requested format.';
      this.showError(input, errorMessage);
    }

    filterDicInput(input) {
      let value = input.value.toUpperCase();
      
      if (value.startsWith('CZ')) {
        const afterCZ = value.substring(2);
        const digitsOnly = afterCZ.replace(/[^0-9]/g, '');
        value = 'CZ' + digitsOnly;
        
        if (value.length > input.maxLength) {
          value = value.substring(0, input.maxLength);
        }
        
        if (input.value !== value) {
          input.value = value;
        }
      } else if (value.startsWith('SK')) {
        const afterSK = value.substring(2);
        const digitsOnly = afterSK.replace(/[^0-9]/g, '');
        value = 'SK' + digitsOnly;
        
        if (value.length > input.maxLength) {
          value = value.substring(0, input.maxLength);
        }
        
        if (input.value !== value) {
          input.value = value;
        }
      } else if (value.length > 0 && !value.startsWith('CZ') && !value.startsWith('SK')) {
        // Filter to digits only, but don't auto-prefix - let user type CZ or SK manually
        const digitsOnly = value.replace(/[^0-9]/g, '');
        if (digitsOnly.length > 0 && input.value !== digitsOnly) {
          if (digitsOnly.length > input.maxLength) {
            input.value = digitsOnly.substring(0, input.maxLength);
          } else {
            input.value = digitsOnly;
          }
        }
      }
    }

    filterIcoInput(input) {
      const value = input.value.replace(/[^0-9]/g, '');
      if (input.value !== value) {
        input.value = value;
      }
    }

    validateAllInputs() {
      const inputs = this.querySelectorAll('.company-billing-info__input');
      inputs.forEach((input) => {
        this.validateInput(input);
      });
    }

    hideError(input) {
      const errorElement = input.parentElement.querySelector('[data-error-element]');
      if (errorElement) {
        errorElement.textContent = '';
        errorElement.classList.remove('is-visible');
        input.removeAttribute('aria-invalid');
        input.setCustomValidity('');
      }
    }

    handleInputChange(evt) {
      const inputs = this.querySelectorAll('.company-billing-info__input');
      const attributes = {};
      let hasAnyValue = false;
      let hasValidationErrors = false;

      inputs.forEach((inp) => {
        const value = inp.value.trim();
        const attrName = inp.name.replace('attributes[', '').replace(']', '');
        attributes[attrName] = value;
        
        if (value !== '') {
          hasAnyValue = true;
          // Check if this input has validation errors (including DIČ format validation)
          if (!this.isInputValid(inp, value)) {
            hasValidationErrors = true;
          }
        }
      });

      // Skip update if all fields are empty
      if (!hasAnyValue) {
        return;
      }

      // Skip update if there are validation errors
      if (hasValidationErrors) {
        return;
      }

      this.fetchRequestOpts.body = JSON.stringify({ attributes });
      
      fetch(theme.routes.cartUpdate, this.fetchRequestOpts)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
        })
        .catch((error) => {
          console.error('Company Billing Info - Error updating cart attributes:', error);
        });
    }

  }

  customElements.define('company-billing-info', CompanyBillingInfo);
}

